import React, { createContext, useContext, useEffect, useState } from 'react';
import { authService } from '../services/authService';
import { profileService } from '../services/profileService';
import { debugProfile } from '../utils/debugHelper';
import { VALID_ROLES, DEFAULT_ROLE, USER_ROLES, isValidRole } from '../constants/roles';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);

  useEffect(() => {
    let mounted = true;
    let timeoutId;
    let initialLoadComplete = false;
    let expectingSignIn = false; // Track if we're expecting a SIGNED_IN from user action

    // Fallback timeout to prevent infinite loading
    const setLoadingTimeout = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (mounted) {
          console.warn('Auth loading timeout - setting loading to false');
          setLoading(false);
        }
      }, 10000); // 10 second timeout
    };

    setLoadingTimeout();

    async function getInitialSession() {
      try {
        console.log('🔄 Getting initial session...');
        const session = await authService.getSession();
        console.log('🔄 Initial session result:', !!session);
        
        if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);
          
          if (session?.user) {
            console.log('🔍 Loading profile for user:', session.user.id);
            
            // Wait for Supabase client to be fully initialized
            await new Promise(resolve => setTimeout(resolve, 500));
            
            try {
              const userProfile = await profileService.getProfile(session.user.id);
              console.log('🔍 Profile lookup result:', userProfile ? 'Found' : 'Not found');
              
              if (userProfile) {
                console.log('✅ Profile loaded:', { role: userProfile.role_type, email: userProfile.email });
                setProfile(userProfile);
              } else {
                console.log('🔧 Creating new profile for user');
                // Profile doesn't exist, create it
                const roleType = session.user.user_metadata?.role_type;
                const validRole = isValidRole(roleType) ? roleType : DEFAULT_ROLE;
                
                const profileData = {
                  id: session.user.id,
                  email: session.user.email,
                  display_name: session.user.user_metadata?.display_name,
                  role_type: validRole,
                  email_verified: session.user.email_confirmed_at !== null
                };
                console.log('🔧 Profile data to create:', profileData);
                
                const newProfile = await profileService.upsertProfile(profileData);
                console.log('✅ Profile created:', newProfile);
                setProfile(newProfile);
              }
            } catch (error) {
              console.error('❌ Error loading/creating profile:', error);
              console.error('❌ Error details:', {
                message: error.message,
                code: error.code,
                details: error.details,
                hint: error.hint
              });
              setProfile(null);
            }
          } else {
            setProfile(null);
          }
          
          if (timeoutId) clearTimeout(timeoutId);
          setLoading(false);
          initialLoadComplete = true;
        }
      } catch (error) {
        console.error('Error getting initial session:', error);
        if (mounted) {
          setSession(null);
          setUser(null);
          setProfile(null);
          if (timeoutId) clearTimeout(timeoutId);
          setLoading(false);
          initialLoadComplete = true;
        }
      }
    }

    getInitialSession();

    const authListener = authService.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 Auth state change:', event, !!session);
        if (!mounted) return;

        setSession(session);
        setUser(session?.user ?? null);

        // Skip events that don't need profile reload
        if (event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') {
          console.log(`🔄 Skipping profile load on ${event} event`);
          return;
        }

        // For SIGNED_IN: only load profile if it's from user action or initial load
        if (event === 'SIGNED_IN') {
          if (!initialLoadComplete) {
            console.log('🔄 Skipping SIGNED_IN during initial load');
            return;
          }
          if (!expectingSignIn) {
            console.log('🔄 Skipping SIGNED_IN from tab visibility');
            return;
          }
          // Reset flag and proceed to load profile
          expectingSignIn = false;
          console.log('🔄 Loading profile after user sign-in');
        }

        if (session?.user) {
          try {
            // Load existing profile
            let userProfile = await profileService.getProfile(session.user.id);
            
            if (userProfile) {
              setProfile(userProfile);
            } else {
              // Profile doesn't exist, create it
              const roleType = session.user.user_metadata?.role_type;
              const validRole = isValidRole(roleType) ? roleType : DEFAULT_ROLE;
              
              const newProfile = await profileService.upsertProfile({
                id: session.user.id,
                email: session.user.email,
                display_name: session.user.user_metadata?.display_name,
                role_type: validRole,
                email_verified: session.user.email_confirmed_at !== null
              });
              
              setProfile(newProfile);
            }
          } catch (error) {
            console.error('Error loading/creating profile:', error);
            setProfile(null);
          }
        } else {
          setProfile(null);
        }

        if (timeoutId) clearTimeout(timeoutId);
        setLoading(false);
      }
    );

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      
      // Safer cleanup - catch any errors from browser extensions
      try {
        if (authListener?.data?.subscription?.unsubscribe) {
          authListener.data.subscription.unsubscribe();
        } else if (authListener?.subscription?.unsubscribe) {
          authListener.subscription.unsubscribe();
        } else if (typeof authListener?.unsubscribe === 'function') {
          authListener.unsubscribe();
        }
      } catch (error) {
        console.warn('Error cleaning up auth listener (likely browser extension conflict):', error);
      }
    };
  }, []);

  const signUp = async ({ email, password, displayName, roleType }) => {
    setLoading(true);
    try {
      const result = await authService.signUp({
        email,
        password,
        displayName,
        roleType
      });
      return result;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signIn = async ({ email, password }) => {
    setLoading(true);
    // Note: This won't work because signIn is outside the useEffect scope
    // We'll need a different approach
    try {
      const result = await authService.signIn({ email, password });
      
      // Manually load profile after successful sign-in
      if (result?.user) {
        try {
          const userProfile = await profileService.getProfile(result.user.id);
          if (userProfile) {
            setProfile(userProfile);
          }
        } catch (error) {
          console.error('Error loading profile after sign-in:', error);
        }
      }
      
      return result;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      console.log('🔄 Starting sign out process...');
      await authService.signOut();
      
      // Force clear state immediately after sign out
      setSession(null);
      setUser(null);
      setProfile(null);
      
      console.log('🔄 Sign out completed');
    } catch (error) {
      console.error('Sign out error:', error);
      // Even if sign out fails, clear local state
      setSession(null);
      setUser(null);
      setProfile(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates) => {
    if (!user) throw new Error('No user logged in');
    
    try {
      const updatedProfile = await profileService.updateProfile(user.id, updates);
      setProfile(updatedProfile);
      return updatedProfile;
    } catch (error) {
      throw error;
    }
  };

  const resetPassword = async (email) => {
    return await authService.resetPassword(email);
  };

  const resendConfirmation = async (email) => {
    return await authService.resendConfirmation(email);
  };

  // Check if user has required role
  const hasRole = (requiredRole) => {
    if (!profile) return false;
    // Validate that requiredRole is valid
    if (!isValidRole(requiredRole)) {
      console.warn(`Invalid role requested: ${requiredRole}. Valid roles are: ${VALID_ROLES.join(', ')}`);
      return false;
    }
    return profile.role_type === requiredRole;
  };

  // Check if user can access admin features
  const isAdmin = () => {
    return profile?.role_type === USER_ROLES.ADMIN;
  };

  // Check if email is verified
  const isEmailVerified = () => {
    return profile?.email_verified === true;
  };

  // Refresh profile data from database
  const refreshProfile = async () => {
    if (!user) return;
    try {
      const userProfile = await profileService.getProfile(user.id);
      if (userProfile) {
        setProfile(userProfile);
      }
    } catch (error) {
      console.error('Error refreshing profile:', error);
    }
  };

  const value = {
    user,
    profile,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    updateProfile,
    refreshProfile,
    resetPassword,
    resendConfirmation,
    hasRole,
    isAdmin,
    isEmailVerified
  };

  // Debug logging (remove this in production)
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 AuthContext State:', { 
      user: !!user, 
      profile: !!profile, 
      profileRole: profile?.role_type,
      loading,
      sessionExists: !!session 
    });
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};